"use client";

import {useCallback, useEffect, useRef, useState} from "react";
import {
  ConversationOrchestrationClient,
  ConversationRequestError,
  ConversationSyncError,
  isConversationUnauthenticatedError,
  type ActiveTask,
  type ConversationOrchestrationView,
  type ConversationSummary,
  type MessageReceipt,
} from "@/lib/user/conversation-orchestration";

const COLLECT_DRAFT_STATES = new Set(["waiting_user_input", "waiting_user_confirmation"]);
const POLLED_EXECUTION_STATES = new Set([
  "starting", "queued", "running", "waiting_operation", "waiting_approval",
  "cancelling", "waiting_gate", "stopping", "dispatching",
]);
const statusSyncDelay = (attempt: number) => Math.min(2000, 500 + attempt * 250);

type Options = {active: boolean; selectedThreadID: string | null; locale?: string; conversationScope?: string; onAuthenticationRequired: () => void};
export type WorkflowConversationNotice = "" | "missing" | "restore_failed" | "changed" | "service_unavailable" | "credits_insufficient";
export type DesignWorkflowConversationController = {
  view: ConversationOrchestrationView | null;
  list: ConversationSummary[];
  activeTask: ActiveTask | null;
  loading: boolean;
  listLoading: boolean;
  pending: boolean;
  notice: WorkflowConversationNotice;
  readOnly: boolean;
  readOnlyReason: string;
  writable: boolean;
  newConversationDisabled: boolean;
  confirming: boolean;
  refresh: () => Promise<void>;
  select: (threadID: string) => Promise<boolean>;
  load: (threadID: string) => Promise<boolean>;
  beginDraft: () => void;
  send: (text: string) => Promise<boolean>;
  confirm: () => Promise<boolean>;
  cancel: () => Promise<boolean>;
  acceptWorkflowProposal: () => Promise<boolean>;
  rejectWorkflowProposal: () => Promise<boolean>;
  archive: (threadID: string) => Promise<boolean>;
  hydrateReceipt: (receipt: MessageReceipt) => void;
};

type State = {scope: string; view: ConversationOrchestrationView | null; list: ConversationSummary[]; activeTask: ActiveTask | null; loading: boolean; listLoading: boolean; pending: boolean; notice: WorkflowConversationNotice};
const EMPTY_STATE: State = {scope: "", view: null, list: [], activeTask: null, loading: false, listLoading: false, pending: false, notice: ""};

/**
 * 设计工作台的会话控制器：列表和详情分离，显式 Thread 负责读写，active_task 负责全局新建门禁。
 * 输入：认证状态与受控 selectedThreadID；输出：权威只读状态、历史列表及等待态操作。
 */
export function useDesignWorkflowConversation({active, selectedThreadID, locale = "zh-CN", conversationScope = "", onAuthenticationRequired}: Options): DesignWorkflowConversationController {
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const clientRef = useRef<ConversationOrchestrationClient | null>(null);
  const mutationClientRef = useRef<ConversationOrchestrationClient | null>(null);
  const refreshRequestRef = useRef(0);
  const archivedThreadIDsRef = useRef<Set<string>>(new Set());
  const [state, setState] = useState<State>(EMPTY_STATE);
  const currentState = active && state.scope === conversationScope ? state : {...EMPTY_STATE, loading: active, listLoading: active};
  const view = currentState.view;
  const executionState = view?.focused_execution?.state;

  const update = useCallback((fn: (state: State) => State) => {
    setState((previous) => fn(previous.scope === conversationScope ? previous : {...EMPTY_STATE, scope: conversationScope}));
  }, [conversationScope]);
  const handleError = useCallback((error: unknown, client: ConversationOrchestrationClient) => {
    if (isConversationUnauthenticatedError(error)) { onAuthenticationRequired(); update((s) => ({...s, loading: false, listLoading: false, notice: ""})); return; }
    if (clientRef.current === client) update((s) => ({...s, loading: false, listLoading: false, notice: error instanceof ConversationRequestError && error.status === 404 ? "missing" : "restore_failed"}));
  }, [onAuthenticationRequired, update]);

  const refreshListProjection = useCallback(async (client: ConversationOrchestrationClient) => {
    const listed = await client.list();
    if (clientRef.current !== client) return;
    // Mutation 回执只带当前 Thread；必须另外刷新 owner 列表，才能立即显示新标题并同步全局任务门禁。
    update((s) => ({...s, list: listed.conversations, activeTask: listed.active_task, listLoading: false}));
  }, [update]);

  const refresh = useCallback(async () => {
    const client = clientRef.current; if (!client) return;
    const requestID = ++refreshRequestRef.current;
    update((s) => ({...s, loading: true, listLoading: true, notice: ""}));
    try {
      const listed = await client.list();
      if (requestID !== refreshRequestRef.current || clientRef.current !== client) return;
      update((s) => ({...s, list: listed.conversations, activeTask: listed.active_task, listLoading: false}));
      if (selectedThreadID && !archivedThreadIDsRef.current.has(selectedThreadID)) {
        const detail = await client.select(selectedThreadID);
        if (requestID === refreshRequestRef.current && clientRef.current === client) update((s) => ({...s, view: detail, loading: false, notice: detail ? "" : "missing"}));
      } else {
        // 空白草稿不请求 create/current；只有旧兼容调用显式 refresh 时才恢复 current。
        update((s) => ({...s, view: null, loading: false}));
      }
    } catch (error) { if (requestID === refreshRequestRef.current) handleError(error, client); }
  }, [handleError, selectedThreadID, update]);

  const load = useCallback(async (threadID: string) => {
    const client = clientRef.current; if (!client) return false;
    update((s) => ({...s, loading: true, notice: ""}));
    try { await client.load(threadID); update((s) => ({...s, loading: false})); return true; }
    catch (error) { handleError(error, client); return false; }
  }, [handleError, update]);
  const select = useCallback(async (threadID: string) => {
    const client = clientRef.current; if (!client) return false;
    update((s) => ({...s, loading: true, notice: ""}));
    try { await client.select(threadID); update((s) => ({...s, loading: false})); return true; }
    catch (error) { handleError(error, client); return false; }
  }, [handleError, update]);
  const hydrateReceipt = useCallback((receipt: MessageReceipt) => {
    const client = clientRef.current;
    if (!client || !receipt.conversation?.id) return;
    const requestID = ++refreshRequestRef.current;
    client.hydrateReceipt(receipt);
    update((s) => ({...s, loading: false, notice: ""}));
    // POST 回执先渲染，详情和历史列表再独立补全；两者失败也不重发启动命令。
    void client.load(receipt.conversation.id).catch((error) => {
      if (requestID === refreshRequestRef.current) handleError(error, client);
    });
    void refreshListProjection(client).catch((error) => {
      if (isConversationUnauthenticatedError(error)) handleError(error, client);
    });
  }, [handleError, refreshListProjection, update]);
  const archive = useCallback(async (threadID: string) => {
    const client = clientRef.current; if (!client) return false;
    // 归档会先清除 client 选择；旧 refresh 闭包可能在草稿切换后才执行，已删除 ID 整个页面会话都不得再恢复。
    archivedThreadIDsRef.current.add(threadID);
    try { await client.archive(threadID); }
    catch (error) {
      archivedThreadIDsRef.current.delete(threadID);
      if (isConversationUnauthenticatedError(error)) handleError(error, client);
      // 历史项归档失败不代表当前正在阅读的会话丢失，不能触发 missing 草稿回退。
      else update((s) => ({...s, notice: "restore_failed"}));
      return false;
    }
    try { await refreshListProjection(client); }
    catch (error) {
      // DELETE 已成功，列表重读失败不能阻止当前会话退出；下次刷新再收敛历史投影。
      if (isConversationUnauthenticatedError(error)) handleError(error, client);
      else update((s) => ({...s, list: s.list.filter((item) => item.id !== threadID), listLoading: false}));
    }
    return true;
  }, [handleError, refreshListProjection, update]);
  const beginDraft = useCallback(() => {
    // 删掉当前会话后，迟到的详情刷新不能把旧会话或旧 loading 状态写回新的空白草稿。
    refreshRequestRef.current += 1;
    clientRef.current?.clearSelection();
    update((s) => ({...s, view: null, loading: false, notice: ""}));
  }, [update]);

  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  useEffect(() => {
    if (!active) { clientRef.current?.close(); clientRef.current = null; mutationClientRef.current = null; return; }
    // 读取当前语言而不重建客户端，切换语言不能丢失会话与在途请求。
    const client = new ConversationOrchestrationClient((input, init) => {
      const headers = new Headers(init?.headers);
      headers.set("x-softmap-locale", localeRef.current);
      return fetch(input, {...init, headers});
    }); clientRef.current = client;
    let accepting = false;
    const unsubscribe = client.subscribe((current) => { if (!accepting) return; update((s) => ({...s, view: current, loading: false, notice: current ? "" : s.notice})); });
    accepting = true;
    // selectedThreadID 变化由下方显式 select effect 处理；不能因此销毁 client，否则首条成功后的列表刷新会被 cleanup 丢弃。
    const timer = window.setTimeout(() => void refreshRef.current(), 0);
    return () => { window.clearTimeout(timer); unsubscribe(); client.close(); if (clientRef.current === client) clientRef.current = null; if (mutationClientRef.current === client) mutationClientRef.current = null; };
  }, [active, conversationScope, update]);

  useEffect(() => {
    if (active && selectedThreadID && !archivedThreadIDsRef.current.has(selectedThreadID) && clientRef.current?.selected() !== selectedThreadID) void select(selectedThreadID);
  }, [active, selectedThreadID, select]);

  useEffect(() => {
    if (!active || !executionState) return;
    const client = clientRef.current; if (!client) return;
    if (COLLECT_DRAFT_STATES.has(executionState)) {
      // collect 等待态是可并行资料草稿；详情一到等待态就重新读取 owner 列表，及时解除启动阶段的临时门禁。
      void refreshListProjection(client).catch((error) => {
        if (isConversationUnauthenticatedError(error)) handleError(error, client);
        else if (clientRef.current === client) update((s) => ({...s, listLoading: false}));
      });
      return;
    }
    if (!POLLED_EXECUTION_STATES.has(executionState)) return;
    let attempts = 0; let cancelled = false; let timer = 0;
    const sync = async () => {
      if (cancelled || clientRef.current !== client) return;
      attempts += 1; const requestID = ++refreshRequestRef.current;
      try {
        const current = await client.load(view?.conversation.id ?? selectedThreadID ?? "");
        if (cancelled || clientRef.current !== client) return;
        if (requestID !== refreshRequestRef.current) {
          // 并发详情读取已取代本次响应，但不能因此终止仍在运行的轮询。
          timer = window.setTimeout(() => void sync(), statusSyncDelay(attempts));
          return;
        }
        const next = current?.focused_execution?.state;
        if (!next || !POLLED_EXECUTION_STATES.has(next)) { await refresh(); return; }
        // 长任务持续同步；不能把正常执行时长误判为恢复失败。
        timer = window.setTimeout(() => void sync(), statusSyncDelay(attempts));
      } catch (error) {
        if (cancelled) return;
        if (requestID !== refreshRequestRef.current) {
          timer = window.setTimeout(() => void sync(), statusSyncDelay(attempts));
          return;
        }
        if (isConversationUnauthenticatedError(error)) { handleError(error, client); return; }
        // 短暂断网保留最后的权威视图，退避后自动恢复；不锁死整个输入区。
        update((s) => ({...s, notice: "restore_failed"}));
        timer = window.setTimeout(() => void sync(), 5000);
      }
    };
    timer = window.setTimeout(() => void sync(), statusSyncDelay(0));
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [active, executionState, handleError, refresh, refreshListProjection, selectedThreadID, update, view?.conversation.id]);

  useEffect(() => {
    if (!active || currentState.notice !== "restore_failed") return;
    // 消息已经接收时只重读详情；恢复请求绝不重新派发用户命令。
    const timer = window.setTimeout(() => void refresh(), 3000);
    return () => window.clearTimeout(timer);
  }, [active, currentState.notice, refresh]);

  const runMutation = useCallback(async (operation: (client: ConversationOrchestrationClient) => Promise<ConversationOrchestrationView | null>) => {
    const client = clientRef.current; if (!client || mutationClientRef.current) return false;
    mutationClientRef.current = client; update((s) => ({...s, pending: true, notice: ""}));
    try {
      await operation(client);
      try {
        // 列表刷新失败不能把已经成功的消息误标成失败，否则用户点击重试会重复表达同一业务意图。
        await refreshListProjection(client);
      } catch (listError) {
        if (isConversationUnauthenticatedError(listError)) handleError(listError, client);
        else if (clientRef.current === client) update((s) => ({...s, listLoading: false}));
      }
      return true;
    }
    catch (error) { if (error instanceof ConversationSyncError) { update((s) => ({...s, notice: "restore_failed"})); return true; } handleError(error, client); if (!isConversationUnauthenticatedError(error)) update((s) => ({...s, notice: error instanceof ConversationRequestError && error.code === "credits.insufficient.v1" ? "credits_insufficient" : error instanceof ConversationRequestError && error.status === 409 ? "changed" : "service_unavailable"})); return false; }
    finally { if (mutationClientRef.current === client) mutationClientRef.current = null; update((s) => ({...s, pending: false})); }
  }, [handleError, refreshListProjection, update]);

  const send = useCallback(async (text: string) => {
    const value = text.trim(); if (!value || currentState.pending || currentState.loading) return false;
    // 服务端 read_only 是最终权限边界；前端只做体验性拦截，不能根据 active_task 猜测可写性。
    if (view && view.read_only) return false;
    return runMutation((client) => view ? client.send(value) : client.sendFirst(value));
  }, [currentState.loading, currentState.pending, runMutation, view]);
  const confirm = useCallback(() => runMutation((client) => client.confirm()), [runMutation]);
  const cancel = useCallback(() => runMutation((client) => client.cancel()), [runMutation]);
  const acceptWorkflowProposal = useCallback(() => runMutation((client) => client.acceptWorkflowProposal()), [runMutation]);
  const rejectWorkflowProposal = useCallback(() => runMutation((client) => client.rejectWorkflowProposal()), [runMutation]);
  const readOnly = Boolean(view?.read_only);
  return {view, list: currentState.list, activeTask: currentState.activeTask, loading: currentState.loading, listLoading: currentState.listLoading, pending: currentState.pending, notice: currentState.notice, readOnly, readOnlyReason: view?.read_only_reason ?? "", writable: !readOnly && currentState.notice !== "missing" && !currentState.loading, newConversationDisabled: Boolean(currentState.activeTask), confirming: executionState === "waiting_user_confirmation", refresh, select, load, hydrateReceipt, beginDraft, send, confirm, cancel, acceptWorkflowProposal, rejectWorkflowProposal, archive};
}

type WorkflowTimelineMessage = NonNullable<ConversationOrchestrationView["messages"]>[number];

/** 服务端统一提供普通消息、节点问答与结果；前端不再按焦点过滤或重建历史。 */
export function visibleWorkflowMessages(view: ConversationOrchestrationView | null): WorkflowTimelineMessage[] {
  return (view?.messages ?? []).filter((message) => !(message.role === "user" && /^启动「[^」]+」$/.test(message.text.trim())));
}
